import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWidgetChat, type ChatMessage } from "./WidgetChat";
import { imagenPermitida } from "@/lib/imagenesChat";
import type { ApiClient } from "@/lib/api/chatApi";

/* ── Props ── */

interface WidgetBubbleProps {
  api: ApiClient;
  agentId: string;
  agentName: string;
  widgetUid: string;
  context: Record<string, string>;
  contextPromptTemplate: string | null;
  threadTtlHours: number;
  userEmail: string;
}

/* ── Inline styles (matches prototype HTML exactly) ── */

const styles = {
  bubble: {
    position: "fixed" as const,
    bottom: 24,
    right: 24,
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #C8102E, #9B0C23)",
    color: "white",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 16px rgba(230, 57, 70, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
    zIndex: 1000,
    pointerEvents: "auto" as const,
  },
  bubbleHover: {
    transform: "scale(1.08)",
    boxShadow: "0 6px 24px rgba(230, 57, 70, 0.5)",
  },
  panel: {
    position: "fixed" as const,
    bottom: 100,
    right: 24,
    width: 380,
    height: 560,
    maxHeight: "calc(100vh - 120px)",
    borderRadius: 16,
    background: "white",
    color: "#53565A",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "1px solid #D0D0CE",
    boxSizing: "border-box" as const,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    zIndex: 999,
    animation: "widgetSlideUp 0.25s ease-out",
    pointerEvents: "auto" as const,
  },
  header: {
    background: "linear-gradient(135deg, #C8102E, #9B0C23)",
    color: "white",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
    lineHeight: "1.25",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
  },
  headerSubtitle: {
    fontSize: 12,
    opacity: 0.85,
    margin: 0,
  },
  newChatBtn: {
    marginLeft: "auto",
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "white",
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s",
    flexShrink: 0,
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto" as const,
    padding: 16,
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    background: "#f7f8fa",
  },
  msgBase: {
    maxWidth: "80%",
    padding: "10px 14px",
    borderRadius: 12,
    fontSize: 13,
    lineHeight: "1.45",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
  },
  msgBot: {
    alignSelf: "flex-start" as const,
    background: "white",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
    borderBottomLeftRadius: 4,
    color: "#53565A",
  },
  msgUser: {
    alignSelf: "flex-end" as const,
    background: "#C8102E",
    color: "white",
    borderBottomRightRadius: 4,
  },
  inputArea: {
    padding: "10px 12px 6px",
    borderTop: "1px solid #D0D0CE",
    display: "flex",
    gap: 6,
    alignItems: "center",
    background: "white",
  },
  avisoRegistro: {
    padding: "0 12px 8px",
    fontSize: 10,
    lineHeight: "14px",
    color: "#888B8D",
    textAlign: "left" as const,
    background: "white",
  },
  inputWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    border: "1px solid #D0D0CE",
    borderRadius: 24,
    background: "white",
    overflow: "hidden",
  },
  input: {
    flex: 1,
    border: "none",
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    color: "#53565A",
    background: "transparent",
    minWidth: 0,
  },
  sendBtn: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#C8102E",
    color: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s",
    flexShrink: 0,
    marginRight: 4,
  },
  sendBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  thinkingDots: {
    display: "flex",
    gap: 4,
    padding: "10px 14px",
    alignSelf: "flex-start" as const,
    background: "white",
    borderRadius: 12,
    borderBottomLeftRadius: 4,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#888B8D",
  },
} as const;

/* ── Keyframes injected once ── */

const keyframesId = "widget-bubble-keyframes";

function ensureKeyframes() {
  if (document.getElementById(keyframesId)) return;
  const style = document.createElement("style");
  style.id = keyframesId;
  style.textContent = `
    @keyframes widgetSlideUp {
      from { opacity: 0; transform: translateY(16px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes widgetBounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-4px); }
    }
    @keyframes widgetSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .widget-input-field::placeholder {
      color: #888B8D !important;
      opacity: 1 !important;
    }
    ${markdownStyles}
  `;
  document.head.appendChild(style);
}

/* ── SVG Icons ── */

function ChatIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function BotIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="m9 15 3-3 3 3" />
    </svg>
  );
}

// Solo imagenes de origenes propios (lib/imagenesChat.ts): una de fuera se queda
// en su texto alternativo y no se descarga.
const COMPONENTES_MARKDOWN: Components = {
  img: ({ src, alt }) => (imagenPermitida(src) ? <img src={src} alt={alt ?? ""} /> : alt ? <span>{alt}</span> : null),
};

/* ── Markdown styles for widget messages ── */

const markdownStyles = `
.widget-md p { margin: 0 0 8px; }
.widget-md p:last-child { margin-bottom: 0; }
.widget-md ul, .widget-md ol { margin: 4px 0; padding-left: 20px; list-style: revert !important; }
.widget-md ul { list-style-type: disc !important; }
.widget-md ol { list-style-type: decimal !important; }
.widget-md li { margin: 2px 0; display: list-item !important; }
.widget-md h1, .widget-md h2, .widget-md h3, .widget-md h4 {
  font-size: 13px; font-weight: 700; margin: 8px 0 4px;
}
.widget-md code {
  background: #f0f0f0; padding: 1px 4px; border-radius: 3px; font-size: 12px;
}
.widget-md pre { background: #f0f0f0; padding: 8px; border-radius: 6px; overflow-x: auto; margin: 4px 0; }
.widget-md pre code { background: none; padding: 0; }
.widget-md table { border-collapse: collapse; margin: 4px 0; font-size: 12px; }
.widget-md th, .widget-md td { border: 1px solid #D0D0CE; padding: 4px 8px; }
.widget-md th { background: #f7f8fa; font-weight: 600; }
.widget-md blockquote { border-left: 3px solid #D0D0CE; margin: 4px 0; padding-left: 10px; color: #888B8D; }
.widget-md hr { border: none; border-top: 1px solid #D0D0CE; margin: 8px 0; }
`;

/* ── File preview helpers ── */

function FileIcon({ size = 20, stroke = "#EF4444" }: { size?: number; stroke?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function PendingFilePreview({ files, onRemove }: { files: File[]; onRemove: (index: number) => void }) {
  return (
    <>
      {files.map((file, index) => {
        const isImage = file.type.startsWith("image/");
        return (
          <PendingFileItem key={`${file.name}-${index}`} file={file} isImage={isImage} index={index} onRemove={onRemove} />
        );
      })}
    </>
  );
}

function PendingFileItem({ file, isImage, index, onRemove }: { file: File; isImage: boolean; index: number; onRemove: (index: number) => void }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, file]);

  return (
    <div style={{ padding: "6px 12px", background: "#f7f8fa", borderTop: "1px solid #D0D0CE", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#53565A" }}>
      {isImage && previewUrl ? (
        <img src={previewUrl} alt={file.name} style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover" as const }} />
      ) : (
        <div style={{ width: 40, height: 40, borderRadius: 4, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FileIcon />
        </div>
      )}
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{file.name}</span>
      <button
        onClick={() => onRemove(index)}
        style={{ background: "none", border: "none", color: "#888B8D", cursor: "pointer", padding: "0 4px", fontSize: 14, lineHeight: 1 }}
      >
        &times;
      </button>
    </div>
  );
}

/* ── Message component ── */

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";
  return (
    <div
      style={{
        ...styles.msgBase,
        ...(isUser ? styles.msgUser : styles.msgBot),
      }}
    >
      {msg.fileName && (
        <div style={{ marginBottom: msg.content ? 6 : 0 }}>
          {msg.fileIsImage && msg.filePreviewUrl ? (
            <img src={msg.filePreviewUrl} alt={msg.fileName} style={{ width: 120, maxHeight: 120, borderRadius: 6, objectFit: "cover" as const, display: "block" }} />
          ) : (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#FEF2F2", borderRadius: 6, fontSize: 11 }}>
              <FileIcon size={16} />
              <span>{msg.fileName}</span>
            </div>
          )}
        </div>
      )}
      {isUser ? msg.content : (
        <div className="widget-md">
          <Markdown remarkPlugins={[remarkGfm]} components={COMPONENTES_MARKDOWN}>{msg.content}</Markdown>
        </div>
      )}
    </div>
  );
}

function toolLabel(name: string): string {
  const text = name.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function ThinkingIndicator({ tools }: { tools: string[] }) {
  return (
    <div style={{
      ...styles.thinkingDots,
      flexDirection: "column" as const,
      alignItems: "flex-start",
      gap: 6,
      padding: "10px 14px",
    }}>
      {tools.length > 0 ? (
        tools.map((name, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#888B8D" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C8102E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "widgetSpin 1s linear infinite", flexShrink: 0 }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            <span>{toolLabel(name)}&hellip;</span>
          </div>
        ))
      ) : (
        <div style={{ display: "flex", gap: 4 }}>
          <span style={{ ...styles.dot, animation: "widgetBounce 1s infinite", animationDelay: "0ms" }} />
          <span style={{ ...styles.dot, animation: "widgetBounce 1s infinite", animationDelay: "150ms" }} />
          <span style={{ ...styles.dot, animation: "widgetBounce 1s infinite", animationDelay: "300ms" }} />
        </div>
      )}
    </div>
  );
}

/* ── Component ── */

export default function WidgetBubble({
  api,
  agentId,
  agentName,
  widgetUid,
  context,
  contextPromptTemplate,
  threadTtlHours,
  userEmail,
}: WidgetBubbleProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [bubbleHovered, setBubbleHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  ensureKeyframes();

  const chat = useWidgetChat({
    api,
    agentId,
    widgetUid,
    context,
    contextPromptTemplate,
    threadTtlHours,
    userEmail,
  });

  const togglePanel = () => setIsOpen((v) => !v);

  const handleSend = () => {
    if (chat.input.trim() || chat.pendingFiles.length > 0) {
      chat.sendMessage(chat.input);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      chat.attachFiles(Array.from(fileList));
    }
    e.target.value = "";
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles: File[] = [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      chat.attachFiles(pastedFiles);
    }
  };

  // Show thinking indicator if last message is assistant with empty content
  const showThinking =
    chat.isLoading &&
    (chat.messages.length === 0 ||
      (chat.messages[chat.messages.length - 1]?.role === "assistant" &&
        !chat.messages[chat.messages.length - 1]?.content));

  return (
    <>
      {/* Panel */}
      {isOpen && (
        <div style={styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.avatar}>
              <BotIcon />
            </div>
            <div style={styles.headerInfo}>
              <h3 style={styles.headerTitle}>{agentName}</h3>
              <p style={styles.headerSubtitle}>SON·IA</p>
            </div>
            {chat.threadId && (
              <button
                style={styles.newChatBtn}
                title={t("widget.open_full")}
                onClick={() => {
                  const base = window.location.origin;
                  window.open(`${base}/?thread=${chat.threadId}&agent=${agentId}`, "_blank");
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
                }
              >
                <ExpandIcon />
              </button>
            )}
            <button
              style={styles.newChatBtn}
              title={t("widget.new_conversation")}
              onClick={chat.handleNewConversation}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
              }
            >
              <PlusIcon />
            </button>
          </div>

          {/* Messages */}
          <div style={styles.messagesArea}>
            {chat.messages.map((msg, i) => {
              // Skip empty assistant messages (thinking state)
              if (msg.role === "assistant" && !msg.content) return null;
              return <MessageBubble key={i} msg={msg} />;
            })}
            {showThinking && <ThinkingIndicator tools={chat.activeTools} />}
            <div ref={chat.messagesEndRef} />
          </div>

          {/* Pending file indicator */}
          {chat.pendingFiles.length > 0 && (
            <PendingFilePreview files={chat.pendingFiles} onRemove={chat.removeFile} />
          )}

          {/* Input */}
          <div style={styles.inputArea}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.csv,.txt,.tsv,.json,.xml"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={chat.isLoading}
              style={{
                background: "none",
                border: "none",
                color: chat.isLoading ? "#D0D0CE" : "#888B8D",
                cursor: chat.isLoading ? "not-allowed" : "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                pointerEvents: "auto" as const,
              }}
              title={t("widget.attach")}
            >
              <PaperclipIcon />
            </button>
            <div style={styles.inputWrapper}>
              <input
                type="text"
                value={chat.input}
                onChange={(e) => chat.setInput(e.target.value)}
                onKeyDown={chat.handleKeyDown}
                onPaste={handlePaste}
                placeholder={t("widget.placeholder")}
                className="widget-input-field"
                style={styles.input}
                disabled={chat.isLoading}
              />
              <button
                onClick={handleSend}
                disabled={chat.isLoading || (!chat.input.trim() && chat.pendingFiles.length === 0)}
                style={{
                  ...styles.sendBtn,
                  ...(chat.isLoading || (!chat.input.trim() && chat.pendingFiles.length === 0)
                    ? styles.sendBtnDisabled
                    : {}),
                }}
                onMouseEnter={(e) => {
                  if (!chat.isLoading && (chat.input.trim() || chat.pendingFiles.length > 0)) {
                    e.currentTarget.style.background = "#9B0C23";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#C8102E";
                }}
                aria-label={t("widget.send")}
              >
                <SendIcon />
            </button>
            </div>
          </div>

          {/* Aviso de registro (version corta del de ChatThread: el panel es estrecho) */}
          <div style={styles.avisoRegistro}>{t("widget.avisoRegistro")}</div>
        </div>
      )}

      {/* Bubble button */}
      <button
        style={{
          ...styles.bubble,
          ...(bubbleHovered ? styles.bubbleHover : {}),
        }}
        onClick={togglePanel}
        onMouseEnter={() => setBubbleHovered(true)}
        onMouseLeave={() => setBubbleHovered(false)}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </button>
    </>
  );
}
